'use client';

import { useRef, useEffect, useState, useCallback } from 'react';
import {
	Brain,
	CheckCircle2,
	MessageSquare,
	MessageSquareText,
	Search,
	ShieldCheck,
	Wrench,
	BarChart3,
	ChevronDown,
	ChevronRight,
	ArrowUpRight,
	Loader2
} from 'lucide-react';
import { geistMonoFont } from '@/_style/fonts';
import { getDataSourceDisplay } from '@/_utils/dataSourceDisplay';
import { EDataSource } from '@/types';
import { DatabaseIcon, ClockIcon } from '@/_components/CitationTooltip/icons';
import SectionLabel from './SectionLabel';

// ─── Demo data constants ────────────────────────────────────────────────────

const DEMO_QUERY = 'What is referendum #415 about and who proposed it?';

const DEMO_STEPS = [
	{
		number: 1,
		title: 'Fetch referendum #415 details',
		source: EDataSource.SUBSQUARE,
		activities: [
			{ icon: 'search', text: 'Searching schema chunks...' },
			{ icon: 'wrench', text: 'Generated GraphQL query' },
			{ icon: 'result', text: 'Found summary and proposer' }
		]
	},
	{
		number: 2,
		title: 'Look up proposer identity',
		source: EDataSource.SUBSCAN,
		activities: [
			{ icon: 'search', text: 'Searching identity schemas...' },
			{ icon: 'wrench', text: 'Generated REST query' },
			{ icon: 'result', text: 'Resolved on-chain identity' }
		]
	}
];

const DEMO_RESPONSE =
	'Referendum #415 proposes allocating 50,000 DOT from the treasury for developer tooling grants [1]. It was proposed by **15oF4uTn...T61pR**, whose verified identity is **"Alice Validator"** [2].';

const DEMO_CITATIONS = [
	{
		index: 1,
		source: EDataSource.SUBSQUARE,
		jsonPath: 'step_1.data.content.summary',
		value: 'Treasury allocation for developer tooling',
		fetchedAt: '12:34:01'
	},
	{
		index: 2,
		source: EDataSource.SUBSCAN,
		jsonPath: 'step_2.data.identity.display',
		value: 'Alice Validator',
		fetchedAt: '12:34:03'
	}
];

const DEMO_SUGGESTIONS = ['How are people voting on this?', 'What other proposals did they make?'];

const HOW_IT_WORKS_STEPS = [
	{
		number: '01',
		icon: <MessageSquareText className='h-5 w-5' />,
		title: 'Ask in plain English',
		desc: 'Type any question about Polkadot: governance, staking, prices, or how things work.'
	},
	{
		number: '02',
		icon: <Search className='h-5 w-5' />,
		title: 'Sources searched automatically',
		desc: 'Your query routes to the right data sources. Complex questions chain multiple sources together.'
	},
	{
		number: '03',
		icon: <ShieldCheck className='h-5 w-5' />,
		title: 'Get a cited answer',
		desc: 'Every fact traced to its source with inline citations. Click any citation to see the raw data.'
	}
];

// ─── Types ──────────────────────────────────────────────────────────────────

type DemoPhase = 'idle' | 'typing' | 'thinking' | 'tab-switch' | 'response' | 'citation-hover' | 'provenance' | 'done';
type DemoTab = 'thinking' | 'provenance' | 'response';

interface CursorState {
	x: number; // px from left of panel
	y: number; // px from top of panel
	visible: boolean;
	clicking: boolean;
}

// ─── Sub-component: Animated cursor ─────────────────────────────────────────

function DemoCursor({ x, y, visible, clicking }: CursorState) {
	return (
		<div
			className='pointer-events-none absolute z-20'
			style={{
				left: x,
				top: y,
				opacity: visible ? 1 : 0,
				transition: 'left 0.6s cubic-bezier(0.4, 0, 0.2, 1), top 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
				willChange: 'left, top, opacity'
			}}
		>
			{/* Cursor arrow SVG */}
			<svg
				width='24'
				height='24'
				viewBox='0 0 24 24'
				fill='none'
				className='drop-shadow-lg'
				style={{
					transform: clicking ? 'scale(0.8)' : 'scale(1)',
					transition: 'transform 0.12s ease'
				}}
			>
				<path
					d='M5 3l14 8.5-6.5 1.5-3.5 6z'
					fill='white'
					stroke='#1a1a1a'
					strokeWidth='1.5'
					strokeLinejoin='round'
				/>
			</svg>
			{/* Click ring — blue expanding circle */}
			{clicking && (
				<span
					className='absolute top-1 left-1 h-5 w-5 rounded-full border-2 border-blue-400'
					style={{ animation: 'cursorClickRing 0.5s ease-out forwards' }}
				/>
			)}
		</div>
	);
}

// ─── Helper: activity icon ─────────────────────────────────────────────────

function getActivityIcon(type: string) {
	const cls = 'h-3.5 w-3.5 text-muted-foreground flex-shrink-0';
	switch (type) {
		case 'search':
			return <Search className={cls} />;
		case 'wrench':
			return <Wrench className={cls} />;
		case 'result':
			return <BarChart3 className={cls} />;
		default:
			return null;
	}
}

// ─── Helper: render response text with bold + citations ────────────────────

function renderResponseParts(text: string, hoveredCitation: number | null) {
	return text.split(/(\*\*.*?\*\*|\[\d+\])/g).map((part, i) => {
		if (part.startsWith('**') && part.endsWith('**')) {
			return (
				<span
					key={i}
					className='text-foreground font-semibold'
				>
					{part.slice(2, -2)}
				</span>
			);
		}
		if (/^\[\d+\]$/.test(part)) {
			const num = parseInt(part.slice(1, -1), 10);
			const isHovered = hoveredCitation === num;
			return (
				<span
					key={i}
					data-cursor-target={`citation-${num}`}
					className={`mx-0.5 inline-flex h-4 w-4 items-center justify-center rounded-sm text-[10px] font-medium transition-colors duration-200 ${
						isHovered ? 'bg-blue-500/40 text-blue-300' : 'bg-blue-500/20 text-blue-400'
					}`}
				>
					{num}
				</span>
			);
		}
		return <span key={i}>{part}</span>;
	});
}

// ─── Sub-component: Tab Bar ────────────────────────────────────────────────

function DemoTabBar({
	activeTab,
	phase,
	citationCount
}: {
	activeTab: DemoTab;
	phase: DemoPhase;
	citationCount: number;
}) {
	const tabBase =
		'flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-all whitespace-nowrap flex-shrink-0';
	const tabActive = 'border-b-2 border-blue-500 bg-secondary text-foreground';
	const tabInactive = 'text-muted-foreground';

	const isThinking = phase === 'thinking';
	const isResponding = phase === 'response' || phase === 'tab-switch';

	return (
		<div className='border-border flex border-b'>
			<div className={`${tabBase} ${activeTab === 'thinking' ? tabActive : tabInactive}`}>
				<Brain
					className='h-3.5 w-3.5 flex-shrink-0'
					strokeWidth={1.5}
				/>
				<span>Thinking</span>
				{isThinking && <span className='ml-0.5 inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500' />}
			</div>
			<div
				data-cursor-target='provenance-tab'
				className={`${tabBase} ${activeTab === 'provenance' ? tabActive : tabInactive}`}
			>
				<Search
					className='h-3.5 w-3.5 flex-shrink-0'
					strokeWidth={1.5}
				/>
				<span>Provenance</span>
				{citationCount > 0 && (
					<span className='bg-secondary text-foreground ml-0.5 rounded-full px-1.5 text-[10px] font-medium'>
						{citationCount}
					</span>
				)}
			</div>
			<div
				data-cursor-target='response-tab'
				className={`${tabBase} ${activeTab === 'response' ? tabActive : tabInactive}`}
			>
				<MessageSquare
					className='h-3.5 w-3.5 flex-shrink-0'
					strokeWidth={1.5}
				/>
				<span>Response</span>
				{isResponding && <span className='ml-0.5 inline-flex h-1.5 w-1.5 rounded-full bg-green-500' />}
			</div>
		</div>
	);
}

// ─── Sub-component: Thinking tab content ───────────────────────────────────

function DemoThinkingContent({
	visibleSteps,
	visibleActivities,
	showPlanCard
}: {
	visibleSteps: number;
	visibleActivities: number[];
	showPlanCard: boolean;
}) {
	return (
		<div className='space-y-2'>
			{/* Query Planning card (collapsed) */}
			{showPlanCard && (
				<div
					className='border-border bg-secondary/50 overflow-hidden rounded-lg border'
					style={{ animation: 'fadeSlideIn 0.3s ease forwards' }}
				>
					<div className='flex items-center gap-3 px-3 py-2.5'>
						<ChevronRight className='text-muted-foreground h-3.5 w-3.5 flex-shrink-0' />
						<CheckCircle2
							className='h-4 w-4 flex-shrink-0 text-emerald-400'
							strokeWidth={2}
						/>
						<span className='text-foreground flex-1 truncate text-xs font-medium'>Query Planning</span>
						<span className='text-muted-foreground text-[10px]'>12:34:00</span>
					</div>
				</div>
			)}

			{/* Step cards */}
			{DEMO_STEPS.map((step, stepIdx) => {
				const ds = getDataSourceDisplay(step.source);
				const isVisible = stepIdx < visibleSteps;
				const allActivitiesDone = step.activities.every((_, actIdx) =>
					visibleActivities.includes(stepIdx * 10 + actIdx)
				);

				return (
					<div
						key={step.number}
						className='transition-all duration-500'
						style={{
							opacity: isVisible ? 1 : 0,
							transform: isVisible ? 'translateY(0)' : 'translateY(8px)'
						}}
					>
						{isVisible && (
							<div className='border-border bg-secondary/50 overflow-hidden rounded-lg border'>
								{/* Step header */}
								<div className='flex items-center gap-2.5 px-3 py-2.5'>
									<ChevronDown className='text-muted-foreground h-3.5 w-3.5 flex-shrink-0' />
									{allActivitiesDone ? (
										<CheckCircle2
											className='h-4 w-4 flex-shrink-0 text-emerald-400'
											strokeWidth={2}
										/>
									) : (
										<Loader2
											className='h-4 w-4 flex-shrink-0 animate-spin text-blue-400'
											strokeWidth={2}
										/>
									)}
									<span className='flex-shrink-0 rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap text-blue-400'>
										Step {step.number}
									</span>
									<span className='text-foreground truncate text-xs font-medium'>{step.title}</span>
									<span
										className={`ml-auto flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${ds.colorClass}`}
									>
										{ds.name}
									</span>
								</div>

								{/* Activities */}
								<div className='border-border/50 bg-card/50 border-t'>
									{step.activities.map((activity, actIdx) => {
										// Encode as stepIdx*10 + actIdx (safe: max 2 steps x 3 activities)
										const actKey = stepIdx * 10 + actIdx;
										const isActVisible = visibleActivities.includes(actKey);
										return (
											<div
												key={actKey}
												className='border-border/30 flex items-center gap-2.5 border-b px-3 py-1.5 last:border-b-0'
												style={{
													opacity: isActVisible ? 1 : 0,
													transform: isActVisible ? 'translateX(0)' : 'translateX(-6px)',
													transition: 'opacity 0.3s ease, transform 0.3s ease'
												}}
											>
												<div className='w-3.5' />
												{getActivityIcon(activity.icon)}
												<span className='text-muted-foreground text-[11px]'>{activity.text}</span>
												{isActVisible && (
													<span className='text-muted-foreground ml-auto text-[10px]'>
														12:34:0{stepIdx * 2 + actIdx + 1}
													</span>
												)}
											</div>
										);
									})}
								</div>
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}

// ─── Sub-component: Response tab content ───────────────────────────────────

function DemoResponseContent({
	showText,
	showSuggestions,
	hoveredCitation
}: {
	showText: boolean;
	showSuggestions: boolean;
	hoveredCitation: number | null;
}) {
	return (
		<div style={{ opacity: showText ? 1 : 0, transition: 'opacity 0.5s ease' }}>
			{/* Response text */}
			<div className='text-foreground text-sm leading-relaxed'>
				{renderResponseParts(DEMO_RESPONSE, hoveredCitation)}
			</div>

			{/* Follow-up suggestions */}
			{showSuggestions && (
				<div
					className='border-border mt-3 flex flex-wrap gap-1.5 border-t pt-2.5'
					style={{ animation: 'fadeSlideIn 0.4s ease forwards' }}
				>
					{DEMO_SUGGESTIONS.map((s) => (
						<span
							key={s}
							className='border-border text-muted-foreground inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px]'
						>
							{s}
							<ArrowUpRight className='h-2.5 w-2.5 flex-shrink-0' />
						</span>
					))}
				</div>
			)}
		</div>
	);
}

// ─── Sub-component: Citation tooltip ───────────────────────────────────────

function DemoCitationTooltip({ citation, visible }: { citation: (typeof DEMO_CITATIONS)[0]; visible: boolean }) {
	const { name: sourceName, colorClass: sourceColorClass } = getDataSourceDisplay(citation.source);

	return (
		<div
			className='border-border bg-popover z-10 w-[260px] rounded-lg border p-2.5 shadow-xl'
			style={{
				opacity: visible ? 1 : 0,
				transform: visible ? 'translateY(0)' : 'translateY(-4px)',
				transition: 'opacity 0.3s ease, transform 0.3s ease',
				pointerEvents: 'none'
			}}
		>
			{/* Header */}
			<div className='border-border mb-2 flex items-center gap-2 border-b pb-2'>
				<span className='text-muted-foreground'>{DatabaseIcon}</span>
				<span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${sourceColorClass}`}>
					{sourceName}
				</span>
			</div>
			{/* JSON Path */}
			<div className='mb-2'>
				<code className='block text-[11px] break-all text-sky-400'>{citation.jsonPath}</code>
			</div>
			{/* Value */}
			<div className='bg-secondary/50 mb-2 rounded p-1.5'>
				<p className='text-foreground text-xs break-words'>{citation.value}</p>
			</div>
			{/* Timestamp */}
			<div className='text-muted-foreground flex items-center gap-1.5 text-[10px]'>
				{ClockIcon}
				<span>Fetched at {citation.fetchedAt}</span>
			</div>
		</div>
	);
}

// ─── Sub-component: Provenance tab content ─────────────────────────────────

function DemoProvenanceContent({ highlightedIndex }: { highlightedIndex: number | null }) {
	return (
		<div className='space-y-2'>
			{DEMO_STEPS.map((step, stepIdx) => {
				const ds = getDataSourceDisplay(step.source);
				const stepCitations = DEMO_CITATIONS.filter((c) => c.jsonPath.startsWith(`step_${step.number}.`));
				const isExpanded = stepIdx === 0;

				return (
					<div
						key={step.number}
						className='border-border bg-secondary/30 overflow-hidden rounded-lg border'
						style={{ animation: `fadeSlideIn ${0.3 + stepIdx * 0.15}s ease forwards` }}
					>
						{/* Step header */}
						<div className='flex items-center gap-2.5 px-3 py-2.5'>
							{isExpanded ? (
								<ChevronDown className='text-muted-foreground h-3.5 w-3.5 flex-shrink-0' />
							) : (
								<ChevronRight className='text-muted-foreground h-3.5 w-3.5 flex-shrink-0' />
							)}
							<span className='flex h-5 w-5 items-center justify-center rounded-full bg-violet-500/20 text-[10px] font-semibold text-violet-400'>
								{step.number}
							</span>
							<span className='text-foreground flex-1 truncate text-xs font-medium'>{step.title}</span>
							<span className='text-muted-foreground rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px]'>
								{stepCitations.length} citation{stepCitations.length !== 1 ? 's' : ''}
							</span>
							<span
								className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${ds.colorClass}`}
							>
								{ds.name}
							</span>
						</div>

						{/* Citations list (only for expanded step) */}
						{isExpanded && (
							<div className='border-border/50 bg-card/50 border-t'>
								{stepCitations.map((cit) => {
									const isHighlighted = highlightedIndex === cit.index;
									return (
										<div
											key={cit.index}
											className={`flex items-center gap-2.5 px-3 py-2 transition-colors duration-300 ${
												isHighlighted ? 'border-l-2 border-cyan-400 bg-cyan-500/10' : 'border-l-2 border-transparent'
											}`}
										>
											<span className='inline-flex h-4 w-4 items-center justify-center rounded-sm bg-cyan-500/20 text-[10px] font-medium text-cyan-400'>
												{cit.index}
											</span>
											<code className='flex-1 truncate text-[11px] text-sky-400'>{cit.jsonPath}</code>
											<span className='text-muted-foreground max-w-[120px] truncate text-[10px]'>{cit.value}</span>
										</div>
									);
								})}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}

// ─── Main component ────────────────────────────────────────────────────────

export default function QueryDemoSection() {
	const sectionRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	// Track inner timeouts (activity animations, click reset) for cleanup
	const innerTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

	// Animation state
	const [phase, setPhase] = useState<DemoPhase>('idle');
	const [activeTab, setActiveTab] = useState<DemoTab>('thinking');
	const [typedChars, setTypedChars] = useState(0);
	const [showPlanCard, setShowPlanCard] = useState(false);
	const [visibleSteps, setVisibleSteps] = useState(0);
	const [visibleActivities, setVisibleActivities] = useState<number[]>([]);
	const [showResponseText, setShowResponseText] = useState(false);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [hoveredCitation, setHoveredCitation] = useState<number | null>(null);
	const [showTooltip, setShowTooltip] = useState(false);
	const [provenanceHighlight, setProvenanceHighlight] = useState<number | null>(null);

	// Tooltip position — computed from citation badge position
	const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

	// Cursor state — positions in px relative to panelRef
	const [cursor, setCursor] = useState<CursorState>({ x: 0, y: 0, visible: false, clicking: false });

	// Move cursor to the center of a target element found by data-cursor-target attribute
	const moveCursorTo = useCallback((targetName: string) => {
		const panel = panelRef.current;
		if (!panel) return;
		const target = panel.querySelector(`[data-cursor-target="${targetName}"]`);
		if (!target) return;
		const panelRect = panel.getBoundingClientRect();
		const targetRect = target.getBoundingClientRect();
		setCursor((prev) => ({
			...prev,
			x: targetRect.left - panelRect.left + targetRect.width / 2,
			y: targetRect.top - panelRect.top + targetRect.height / 2,
			visible: true
		}));
	}, []);

	// Move cursor to a fixed px offset within the panel (for idle positions)
	const moveCursorToOffset = useCallback((x: number, y: number) => {
		setCursor((prev) => ({ ...prev, x, y, visible: true }));
	}, []);

	// Trigger click animation
	const triggerClick = useCallback(() => {
		setCursor((prev) => ({ ...prev, clicking: true }));
		const id = setTimeout(() => setCursor((prev) => ({ ...prev, clicking: false })), 250);
		innerTimersRef.current.push(id);
	}, []);

	// Hide cursor
	const hideCursor = useCallback(() => {
		setCursor((prev) => ({ ...prev, visible: false }));
	}, []);

	// Compute tooltip position relative to the tab content area when tooltip becomes visible
	useEffect(() => {
		if (showTooltip && panelRef.current) {
			const citation = panelRef.current.querySelector('[data-cursor-target="citation-1"]');
			const contentArea = panelRef.current.querySelector('[data-tab-content]');
			if (citation && contentArea) {
				const citRect = citation.getBoundingClientRect();
				const contentRect = contentArea.getBoundingClientRect();
				setTooltipPos({
					top: citRect.bottom - contentRect.top + 10,
					left: Math.max(0, citRect.left - contentRect.left - 40)
				});
			}
		} else {
			setTooltipPos(null);
		}
	}, [showTooltip]);

	// Phase 0: Trigger on scroll
	useEffect(() => {
		const el = sectionRef.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setPhase('typing');
					observer.disconnect();
				}
			},
			{ threshold: 0.3 }
		);

		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	// Phase 1: Typing animation — cursor at search bar
	useEffect(() => {
		if (phase !== 'typing') return;
		if (typedChars === 0) moveCursorTo('search-bar');
		if (typedChars >= DEMO_QUERY.length) {
			const timer = setTimeout(() => setPhase('thinking'), 400);
			return () => clearTimeout(timer);
		}
		const timer = setTimeout(() => setTypedChars((c) => c + 1), 30);
		return () => clearTimeout(timer);
	}, [phase, typedChars, moveCursorTo]);

	// Phase 2: Thinking — cursor idles while steps appear, then moves to Response tab
	useEffect(() => {
		if (phase !== 'thinking') return;

		setActiveTab('thinking');
		// Idle in the middle of the content area
		const panel = panelRef.current;
		if (panel) {
			moveCursorToOffset(panel.offsetWidth * 0.6, panel.offsetHeight * 0.55);
		}

		const planTimer = setTimeout(() => setShowPlanCard(true), 300);

		const step1Timer = setTimeout(() => {
			setVisibleSteps(1);
			DEMO_STEPS[0].activities.forEach((_, actIdx) => {
				const id = setTimeout(() => setVisibleActivities((prev) => [...prev, actIdx]), (actIdx + 1) * 300);
				innerTimersRef.current.push(id);
			});
		}, 800);

		const step2Timer = setTimeout(() => {
			setVisibleSteps(2);
			DEMO_STEPS[1].activities.forEach((_, actIdx) => {
				const id = setTimeout(() => setVisibleActivities((prev) => [...prev, 10 + actIdx]), (actIdx + 1) * 300);
				innerTimersRef.current.push(id);
			});
		}, 2200);

		// Move cursor to Response tab before switching
		const cursorMoveTimer = setTimeout(() => moveCursorTo('response-tab'), 3400);
		const switchTimer = setTimeout(() => setPhase('tab-switch'), 3800);

		return () => {
			clearTimeout(planTimer);
			clearTimeout(step1Timer);
			clearTimeout(step2Timer);
			clearTimeout(cursorMoveTimer);
			clearTimeout(switchTimer);
			innerTimersRef.current.forEach(clearTimeout);
			innerTimersRef.current = [];
		};
	}, [phase, moveCursorTo, moveCursorToOffset]);

	// Phase 3: Click the Response tab
	useEffect(() => {
		if (phase !== 'tab-switch') return;
		triggerClick();
		setActiveTab('response');
		const timer = setTimeout(() => setPhase('response'), 500);
		return () => clearTimeout(timer);
	}, [phase, triggerClick]);

	// Phase 4: Show response text — cursor moves to citation [1]
	useEffect(() => {
		if (phase !== 'response') return;
		const textTimer = setTimeout(() => setShowResponseText(true), 200);
		// Move cursor toward citation [1] after text appears
		const cursorTimer = setTimeout(() => moveCursorTo('citation-1'), 1000);
		const suggestTimer = setTimeout(() => setShowSuggestions(true), 1200);
		const nextTimer = setTimeout(() => setPhase('citation-hover'), 2000);
		return () => {
			clearTimeout(textTimer);
			clearTimeout(cursorTimer);
			clearTimeout(suggestTimer);
			clearTimeout(nextTimer);
		};
	}, [phase, moveCursorTo]);

	// Phase 5: Cursor hovers citation — tooltip appears
	useEffect(() => {
		if (phase !== 'citation-hover') return;
		const hoverTimer = setTimeout(() => {
			setHoveredCitation(1);
			setShowTooltip(true);
		}, 300);
		const unhoverTimer = setTimeout(() => {
			setShowTooltip(false);
			setHoveredCitation(null);
		}, 2800);
		// Move cursor to Provenance tab
		const cursorTimer = setTimeout(() => moveCursorTo('provenance-tab'), 3000);
		const nextTimer = setTimeout(() => setPhase('provenance'), 3400);
		return () => {
			clearTimeout(hoverTimer);
			clearTimeout(unhoverTimer);
			clearTimeout(cursorTimer);
			clearTimeout(nextTimer);
		};
	}, [phase, moveCursorTo]);

	// Phase 6: Click the Provenance tab
	useEffect(() => {
		if (phase !== 'provenance') return;
		triggerClick();
		setActiveTab('provenance');
		const highlightTimer = setTimeout(() => setProvenanceHighlight(1), 500);
		const cursorFadeTimer = setTimeout(() => hideCursor(), 2200);
		const doneTimer = setTimeout(() => setPhase('done'), 3000);
		return () => {
			clearTimeout(highlightTimer);
			clearTimeout(cursorFadeTimer);
			clearTimeout(doneTimer);
		};
	}, [phase, triggerClick, hideCursor]);

	// Phase 7: Loop — reset everything and restart from typing
	useEffect(() => {
		if (phase !== 'done') return;
		const loopTimer = setTimeout(() => {
			innerTimersRef.current.forEach(clearTimeout);
			innerTimersRef.current = [];
			setTypedChars(0);
			setShowPlanCard(false);
			setVisibleSteps(0);
			setVisibleActivities([]);
			setShowResponseText(false);
			setShowSuggestions(false);
			setHoveredCitation(null);
			setShowTooltip(false);
			setTooltipPos(null);
			setProvenanceHighlight(null);
			setActiveTab('thinking');
			setCursor({ x: 0, y: 0, visible: false, clicking: false });
			setPhase('typing');
		}, 2000);
		return () => clearTimeout(loopTimer);
	}, [phase]);

	// Determine what to show based on phase progression
	const showTabs = phase !== 'idle' && phase !== 'typing';

	return (
		<section
			ref={sectionRef}
			id='demo'
			className='relative scroll-mt-16 px-6 py-20 sm:px-10 md:py-28 lg:px-20'
		>
			{/* Subtle background gradient */}
			<div className='pointer-events-none absolute inset-0'>
				<div className='absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-500/[0.03] blur-[150px]' />
			</div>

			<div className='relative mx-auto max-w-6xl'>
				{/* Two-column layout: text left, demo right */}
				<div className='grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16'>
					{/* Left: context */}
					<div className='flex flex-col justify-center lg:justify-start lg:pt-6'>
						<SectionLabel>How it works</SectionLabel>
						<h2 className={`${geistMonoFont.className} text-foreground text-2xl font-bold sm:text-3xl md:text-4xl`}>
							Three steps to
							<br />
							<span className='text-muted-foreground'>any answer.</span>
						</h2>
						<p className='text-muted-foreground mt-6 text-sm leading-relaxed sm:text-base'>
							No configuration. No switching between tools. Just ask a question and get a verified answer.
						</p>

						{/* 3-step process */}
						<div className='mt-10 space-y-6'>
							{HOW_IT_WORKS_STEPS.map((step) => (
								<div
									key={step.number}
									className='flex gap-4'
								>
									<div className='flex flex-col items-center'>
										<span className={`${geistMonoFont.className} text-lg font-bold text-pink-400/60`}>
											{step.number}
										</span>
									</div>
									<div>
										<div className='text-muted-foreground mb-1.5'>{step.icon}</div>
										<h3 className='text-foreground text-sm font-medium'>{step.title}</h3>
										<p className='text-muted-foreground mt-1 text-xs leading-relaxed'>{step.desc}</p>
									</div>
								</div>
							))}
						</div>

						{/* Why citations matter */}
						<div className='mt-10 border-l-2 border-pink-500/40 pl-4'>
							<p className='text-foreground text-sm font-medium'>Every answer is verifiable.</p>
							<p className='text-muted-foreground mt-1 text-xs leading-relaxed'>
								Unlike generic AI chatbots, Blockpedia traces every claim to its data source. Click any citation to see
								the raw data, source, and JSON path — full data provenance for every fact.
							</p>
						</div>
					</div>

					{/* Right: demo panel */}
					<div
						ref={panelRef}
						className='border-border bg-card/60 relative hidden overflow-hidden rounded-2xl border shadow-2xl shadow-black/40 lg:block lg:self-center'
					>
						{/* Animated cursor */}
						<DemoCursor {...cursor} />

						{/* Mock search bar */}
						<div className='border-border border-b px-5 py-3.5'>
							<div
								data-cursor-target='search-bar'
								className='bg-secondary/60 flex items-center gap-3 rounded-lg px-4 py-2.5'
							>
								<Search className='text-muted-foreground h-4 w-4 flex-shrink-0' />
								<span className='text-foreground text-sm'>
									{phase === 'idle' ? (
										<span className='text-muted-foreground'>Ask a question...</span>
									) : (
										<>
											{DEMO_QUERY.slice(0, typedChars)}
											{phase === 'typing' && <span className='animate-pulse text-pink-400'>|</span>}
										</>
									)}
								</span>
							</div>
						</div>

						{/* Tab bar */}
						{showTabs && (
							<DemoTabBar
								activeTab={activeTab}
								phase={phase}
								citationCount={
									phase === 'thinking' || phase === 'tab-switch'
										? visibleSteps >= 2
											? 2
											: visibleSteps >= 1
												? 1
												: 0
										: 2
								}
							/>
						)}

						{/* Tab content area */}
						<div
							data-tab-content
							className='relative px-4 py-3.5'
							style={{ minHeight: '320px' }}
						>
							{/* Thinking tab */}
							{activeTab === 'thinking' && showTabs && (
								<DemoThinkingContent
									visibleSteps={visibleSteps}
									visibleActivities={visibleActivities}
									showPlanCard={showPlanCard}
								/>
							)}

							{/* Response tab */}
							{activeTab === 'response' && (
								<DemoResponseContent
									showText={showResponseText}
									showSuggestions={showSuggestions}
									hoveredCitation={hoveredCitation}
								/>
							)}

							{/* Citation tooltip — floats near the [1] badge */}
							{activeTab === 'response' && tooltipPos && (
								<div
									className='absolute z-10'
									style={{ top: tooltipPos.top, left: tooltipPos.left }}
								>
									{/* Upward-pointing arrow */}
									<div className='border-b-popover ml-11 h-0 w-0 border-x-[6px] border-b-[6px] border-x-transparent' />
									<DemoCitationTooltip
										citation={DEMO_CITATIONS[0]}
										visible={showTooltip}
									/>
								</div>
							)}

							{/* Provenance tab */}
							{activeTab === 'provenance' && <DemoProvenanceContent highlightedIndex={provenanceHighlight} />}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
